import { StatusObjectEnumType } from 'shared/common/global-types.runtime'
import { gql } from '@apollo/client'
import dig from 'lodash/get'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { PROJECT_STATUS } from 'shared/common/enums'
import {
  ProjectTypeEnumType,
  StatusEnumType,
  UserProjectOrganizationTypeEnumType,
} from 'shared/common/global-types.runtime'
import * as TypeGuards from 'shared/utils/type-guards'
import { isProviderUser, Nullable } from 'shared/utils/type-guards'

import { COLORS, SIZES } from '~/common/globalStyles'
import { useChangeStatusDropdownUpdateNodeStatusMutation } from '~/common/types/graphql'
import dateHelpers from '~/utils/dates'
import endOfDay from '~/utils/gcxDates/endOf/day'
import isAfterDate from '~/utils/gcxDates/isAfterDate'
import { numberHelpers } from '~/utils/numbers'
import { projectHelpers } from '~/utils/project'
import StatusDropdown from '~/views/components/ChangeStatusDropdown'
import { ProjectStatusObservable } from '~/views/components/ChangeStatusDropdown/types'
import CollaborationLogos from '~/views/components/CollaborationLogos'
import {
  CardBody,
  CardHeader,
  SmallLabeledData,
} from '~/views/components/elements/overview'
import AddProjectReasonDialog from '~/views/components/projects/ProjectStatusChangeReasons/AddProjectReasonDialog/AddProjectReasonDialog'
import Link from '~/views/components/routing/Link'
import { TagsBar } from '~/views/components/Tags'
import Card from '~/views/components/ui/Card'
import Icon from '~/views/components/ui/Icon'
import Text from '~/views/components/ui/Text'
import ViewerAuthorized from '~/views/components/ViewerAuthorized/ViewerAuthorized'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import { useFlashMessageContext } from '~/views/contexts/FlashMessageContext'
import useProjectId from '~/views/hooks/useProjectId'

import ProjectProgress from './ProjectProgress'
import { useIsMounted } from 'shared/hooks/useIsMounted'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const Header = styled(CardHeader)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const SharedIconText = styled(Text)`
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(3)};
  display: flex;
  color: ${COLORS.mediumGray};
`
const OverviewGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 335px 1fr;
  grid-template-rows: auto;
  align-items: center;
  grid-gap: ${({ theme }) => theme.spacing(3)};
`

const OverviewLogos = styled(CollaborationLogos)(
  ({ theme }) => `
  border-right: 1px solid ${theme.palette.divider};
  padding-right: ${theme.spacing(3)};
  width: 340px;
`,
)

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const FileIconWrapper = styled.div`
  margin-right: ${({ theme }) => theme.spacing(2)};
`

const FileIconText = styled(Text).attrs({
  'aria-label': 'Project Attachments',
})`
  display: none;
  align-items: center;
  color: ${COLORS.mediumGray};
`
const SharedIcon = styled(Icon)`
  width: ${SIZES.large};
`

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`

const StyledTagBar = styled(TagsBar)`
  margin-left: ${({ theme }) => theme.spacing(3)};
`

type ShareIconProps = {
  labels: {
    // "Share"
    share: string
  }
}
const ShareIcon = ({ labels }: ShareIconProps) => {
  const projectId = useProjectId()
  return (
    <Link
      href={{
        query: { projectId, 'edit-project': true, 'task-tab': 'sharing' },
      }}
    >
      <SharedIconText>
        <SharedIcon name="share" /> &nbsp;{labels.share}
      </SharedIconText>
    </Link>
  )
}

const FileIcon = ({ count = 0 }) => (
  <FileIconWrapper>
    <FileIconText>
      <Icon name="paperclip" />
      {count}
    </FileIconText>
  </FileIconWrapper>
)

export interface Props {
  className?: string
  project?: {
    id: string
    completedAt?: Nullable<Date>
    currentUserProjectContext?: Nullable<UserProjectOrganizationTypeEnumType>
    customer: { id: string; name: string }
    displayOrganization?: Nullable<{ name: string }>
    endOn?: Nullable<Date>
    files?: Nullable<{ id: string }[]>
    firstActiveTemplate?: Nullable<string>
    forecastedEndOn?: Nullable<Date>
    name: string
    projectType?: Nullable<ProjectTypeEnumType>
    startOn?: Date
    status: StatusEnumType
    errorsCount?: number

    organization?: Nullable<{
      id: string
      showShareableLink?: Nullable<boolean>
    }>

    tags: Nullable<
      {
        id: string
        name: string
        color: string
        internalOnly: boolean
      }[]
    >

    templates: Nullable<
      {
        milestones?: Nullable<
          {
            name: string
            active: boolean
          }[]
        >
      }[]
    >
  }
  stats?: {
    internalTasks?: { overdue: number } | null
    customerTasks?: { overdue: number } | null
    thirdPartyTasks?: { overdue: number } | null
    totalTasks: number
    totalDoneTasks: number
    totalSignOffTasks: number
    totalStuckTasks: number
    totalWorkingOnItTasks: number
    totalNotStartedTasks: number
    totalAssignedTasks: number
  }
}

const SummaryCard = ({ className, project, stats }: Props) => {
  const isMounted = useIsMounted()

  const { showFlashMessage } = useFlashMessageContext()
  const { currentUser } = useCurrentUserContext()

  const statusChangeReasonsCompanySettingsIsOn =
    currentUser?.organization?.setting.useStatusChangeReasons || false

  const [showAddReasonDialog, setShowAddReasonDialog] = useState<boolean>(false)
  const [newProjectStatus, setNewProjectStatus] = useState<
    StatusEnumType | undefined
  >(undefined)
  const [currentStatus, setCurrentStatus] = useState<StatusEnumType | string>(
    project?.status || '',
  )

  const [changeStatusDropdownUpdateNodeStatusMutation] =
    useChangeStatusDropdownUpdateNodeStatusMutation()

  const viewOnly = !(
    (currentUser?.isAdmin() ||
      currentUser?.isManager() ||
      currentUser?.isGuide()) &&
    isProviderUser(project?.currentUserProjectContext)
  )

  const templates = project?.templates || []
  const activeMilestones = templates
    .reduce((memo: any[], template) => {
      const milestones =
        template?.milestones?.filter((milestone) => milestone.active) || []

      return [...memo, ...milestones]
    }, [])
    .map((milestone) => milestone.name)

  const timeElapsed = projectHelpers.getTimeElapsed(project)

  const daysTotal =
    project?.startOn && project?.endOn
      ? dateHelpers.daysDiff(project.startOn, project.endOn)
      : 0
  const daysElapsed = project?.startOn
    ? dateHelpers.daysSince(project.startOn)
    : 0
  const daysLeft = project?.endOn
    ? dateHelpers.daysUntil(project.endOn).days
    : 0

  const percentElapsed = Math.min(...[1, daysElapsed / daysTotal])
  const percentElapsedPretty = numberHelpers.prettyPercent(percentElapsed)

  const percentLeft = Math.min(...[1, daysLeft / daysTotal])
  const percentLeftPretty = numberHelpers.prettyPercent(percentLeft)

  const today = new Date()
  const daysDiff = dateHelpers.calendarDaysDiff(today, project?.endOn)

  const [getCommonLabel, getProjectOverviewLabel] = useStaticLabels([
    'common',
    'projectOverview',
  ])

  const calculatedTimeLeft =
    daysDiff < 0
      ? `${daysDiff} ${getCommonLabel('days')} (${getCommonLabel('overdue')})`
      : `${daysDiff} ${getCommonLabel('days')} (${percentLeftPretty})`

  const timeLeft = project?.endOn ? calculatedTimeLeft : 'N/A'
  const projectId = useProjectId()

  const forecastedEndOn = project?.forecastedEndOn
    ? new Date(project.forecastedEndOn)
    : ''

  const projectEndOn = project?.endOn ? new Date(project.endOn) : ''
  const showForecastedEndWarn = useMemo(() => {
    if (forecastedEndOn && projectEndOn) {
      return isAfterDate(endOfDay(forecastedEndOn), endOfDay(projectEndOn))
    }

    return true
  }, [projectEndOn, forecastedEndOn])

  const customer = project?.customer

  // todo: update this to be a dynamic label from the response, but for now, we'll just use a hardcoded string because it's not working
  const markAsDoneMessage =
    'This project has been marked Done. A final project summary will be sent to all team members...'

  return (
    <Card className={className}>
      <Header>
        <FlexRow>
          <Text size="large" weight="bold">
            {getProjectOverviewLabel('projectOverviewTitle')}
          </Text>

          <StyledTagBar tags={project?.tags || []} noMarginTop showTagsText />
        </FlexRow>

        <FlexRow>
          {!viewOnly && (
            <>
              {project?.organization?.showShareableLink && (
                <ShareIcon
                  labels={{
                    share: getCommonLabel('share'),
                  }}
                />
              )}

              <Link href={{ query: { projectId, 'edit-project': true } }}>
                {<FileIcon count={project?.files?.length} />}
              </Link>
            </>
          )}

          {viewOnly && <FileIcon count={project?.files?.length} />}

          <StatusDropdown
            data-testid="project-status-dropdown-button"
            status={currentStatus}
            projectId={project?.id}
            isProject
            disabled={viewOnly}
            style={{ height: 16 }}
            controlStatus={(
              statusObservable: any,
              updateStatus: (status: any) => void,
            ) => {
              statusObservable.subscribe((value: ProjectStatusObservable) => {
                if (
                  statusChangeReasonsCompanySettingsIsOn &&
                  (value.status === PROJECT_STATUS.late ||
                    value.status === PROJECT_STATUS.onHold ||
                    value.status === PROJECT_STATUS.cancelled)
                ) {
                  setNewProjectStatus(value.status)
                  setShowAddReasonDialog(true)
                } else {
                  updateStatus(value)
                  setCurrentStatus(value.status)
                }
                if (value.status === PROJECT_STATUS.done) {
                  showFlashMessage({
                    message: markAsDoneMessage,
                    variant: 'success',
                  })
                }
              })
            }}
          />
        </FlexRow>
      </Header>

      <CardBody>
        <OverviewGrid>
          <OverviewLogos
            provider={project?.displayOrganization}
            customer={customer}
            isInternalProject={
              project?.projectType === ProjectTypeEnumType.Internal
            }
            labels={{
              provider: getCommonLabel('provider'),
              customer: getCommonLabel('customer'),
            }}
          />
          <div style={{ alignSelf: 'baseline' }}>
            <StatsContainer>
              <SmallLabeledData
                title={getCommonLabel('startDate')}
                value={
                  project?.startOn && isMounted
                    ? dateHelpers.prettyDate(project.startOn)
                    : 'N/A'
                }
              />
              <SmallLabeledData
                title={getCommonLabel('plannedEndDate')}
                value={
                  project?.endOn && isMounted
                    ? dateHelpers.prettyDate(project.endOn)
                    : 'N/A'
                }
              />
              {project?.forecastedEndOn && (
                <SmallLabeledData
                  data-testid="forecasted-end-date"
                  title={getCommonLabel('forecastedEndDate')}
                  value={
                    project?.forecastedEndOn && isMounted
                      ? dateHelpers.prettyDate(project.forecastedEndOn)
                      : ''
                  }
                  color={showForecastedEndWarn ? 'red' : 'green'}
                />
              )}
              <SmallLabeledData
                title={getCommonLabel('timeElapsed')}
                value={
                  project?.startOn
                    ? `${timeElapsed} ${
                        timeElapsed !== 1
                          ? getCommonLabel('days')
                          : getCommonLabel('day')
                      }  (${percentElapsedPretty})`
                    : 'N/A'
                }
              />
              <SmallLabeledData
                title={getCommonLabel('timeLeft')}
                value={timeLeft}
                color={daysDiff < 0 ? 'red' : ''}
              />
            </StatsContainer>
            <ProjectProgress
              disableClick={!currentUser}
              size="huge"
              projectStats={{
                totalTasks: dig(stats, 'totalTasks', 0),
                totalSignOffTasks: dig(stats, 'totalSignOffTasks', 0),
                totalAssignedTasks: dig(stats, 'totalAssignedTasks', 0),
                totalNotStartedTasks: dig(stats, 'totalNotStartedTasks', 0),
                totalStuckTasks: dig(stats, 'totalStuckTasks', 0),
                totalWorkingOnItTasks: dig(stats, 'totalWorkingOnItTasks', 0),
                totalDoneTasks: dig(stats, 'totalDoneTasks', 0),
                totalInternalOverdueTasks: dig(
                  stats,
                  'totalInternalOverdueTasks',
                  0,
                ),
                totalCustomerOverdueTasks: dig(
                  stats,
                  'customerTasks.overdue',
                  0,
                ),
                totalThirdPartyOverdueTasks: dig(
                  stats,
                  'thirdPartyTasks.overdue',
                  0,
                ),
              }}
              projectId={project?.id || ''}
              firstActiveTemplateId={project?.firstActiveTemplate}
            />
            <div style={{ display: 'flex', paddingTop: '21px' }}>
              <div>
                <Text size="medium" weight="bold">
                  {getCommonLabel('activeMilestones')}:
                </Text>
                <Text size="medium">{activeMilestones.join(', ')}</Text>
              </div>
            </div>
          </div>
        </OverviewGrid>
      </CardBody>
      {project &&
        statusChangeReasonsCompanySettingsIsOn &&
        TypeGuards.isProviderUser(project.currentUserProjectContext) && (
          <ViewerAuthorized roles={['ADMIN', 'MANAGER', 'GUIDE']}>
            <AddProjectReasonDialog
              organizationId={project.organization?.id}
              newProjectStatus={newProjectStatus}
              open={showAddReasonDialog}
              onClose={() => {
                setShowAddReasonDialog(false)
              }}
              project={project}
              onSubmit={async () => {
                if (newProjectStatus) {
                  await changeStatusDropdownUpdateNodeStatusMutation({
                    variables: {
                      id: project.id,
                      // using the generic node status mutation which doesn't distinguish between task and project status
                      status: newProjectStatus as unknown as StatusEnumType,
                      objectType: StatusObjectEnumType.Project,
                    },
                  }).then(() => setCurrentStatus(newProjectStatus))
                }
              }}
            />
          </ViewerAuthorized>
        )}
    </Card>
  )
}

SummaryCard.fragments = {
  project: gql`
    fragment SummaryCard_Project on ProjectType {
      id
      firstActiveTemplate
      currentUserProjectContext
      status
      startOn
      endOn
      completedAt
      forecastedEndOn
      displayOrganization {
        name
        logo {
          originalUrl
        }
      }
      customerV2 {
        id
        name
        logo {
          originalUrl
        }
      }
      projectType
      templates {
        id
        name
        milestones {
          id
          name
          active
        }
      }
      files {
        id
      }
      tags {
        id
        color
        name
        internalOnly
      }
      organization {
        id
        showShareableLink
      }
    }
  `,
  stats: gql`
    fragment SummaryCard_ProjectStats on ProjectStats {
      id
      totalTasks
      totalDoneTasks
      totalSignOffTasks
      totalStuckTasks
      totalWorkingOnItTasks
      totalNotStartedTasks
      totalAssignedTasks
      totalCustomerOverdueTasks
      totalThirdPartyOverdueTasks
      totalInternalOverdueTasks
    }
  `,
}

export default SummaryCard
